<template>
  <b-container fluid>
    <b-row>
      <b-col cols="12">
        <h5>Infrastructure Details</h5>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Infrastructure Name:</strong>
              {{infrastructure.name}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Reference Designator:</strong>
              {{infrastructure.reference_designator}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Status:</strong>
              {{getStatusIndicatorText(infrastructure.reference_designator)}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <div class="infra-attribute">
              <strong>Latitude:</strong>
              {{infrastructure.lat}}
            </div>
          </b-col>
          <b-col cols="6">
            <div class="infra-attribute">
              <strong>Longitude:</strong>
              {{infrastructure.lon}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Description:</strong>
              <p v-if="!excerpt">No details available.</p>
              <div v-if="excerpt && pageLink">
                <span v-if="excerpt" v-html="excerpt"></span>
                <a v-if="pageLink" :href="pageLink" target="_blank">
                  <b-button variant="outline-info" size="sm">
                    Learn More
                    <i class="fas fa-external-link-alt"></i>
                  </b-button>
                </a>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <hr />
    <b-row>
      <b-col cols="12">
        <h5>Research Site Details</h5>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Site Name:</strong>
              {{site.properties.site_name}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Research Area:</strong>
              {{site.properties.site_area}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Reference Designator:</strong>
              {{site.properties.reference_designator}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <div class="infra-attribute">
              <strong>Latitude:</strong>
              {{site.properties.lat}}
            </div>
          </b-col>
          <b-col cols="6">
            <div class="infra-attribute">
              <strong>Longitude:</strong>
              {{site.properties.lon}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <div class="infra-attribute">
              <strong>Description:</strong>
              {{site.properties.description}}
              <div v-if="site.properties.URL !== null">
                <a v-if="site.properties.URL" :href="site.properties.URL" target="_blank">
                  <b-button variant="outline-info" size="sm">
                    Learn More
                    <i class="fas fa-external-link-alt"></i>
                  </b-button>
                </a>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <hr/>
    <b-row>
      <b-col cols="12">
        <h5>Research Area Details</h5>
        <b-row>
            <b-col cols="12">
              <b-img
                :src="areaImagesMap[site.properties.area_rd]"
                fluid
                :alt="site.properties.site_area"
                v-b-modal="'research-area-lightbox'">
                </b-img>
            </b-col>
            <b-modal id="research-area-lightbox"
             size="xl"
             header-class="lightbox-header"
             body-class="lightbox-body"
             content-class="lightbox-modal-content"
             hide-footer
             centered>
              <b-container fluid style="padding: 0;">
                <b-row>
                  <b-col cols="12" class="text-center">
                    <b-img
                    :src="areaImagesMap[site.properties.area_rd]"
                    fluid
                    :alt="site.properties.site_area">
                    </b-img>
                  </b-col>
                </b-row>
              </b-container>
            </b-modal>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
/**
 * Infrastructure Details component.
 *
 * This component contains all the interactions
 * for the detail tab in the map detail map window.
 */
import _ from 'lodash';
import { mapState } from 'vuex';
import { infraIcons } from './staticGeom';
import { AREA_IMAGES_MAP } from '../../utils/constants';

export default {
  props: ['infrastructure', 'wpContent', 'site'],
  data() {
    return {
      infraIcons,
      areaImagesMap: AREA_IMAGES_MAP,
    };
  },
  methods: {
    /**
     * Helper method to filter through the statusIndicator information
     * @param {string} infraRD - infrastructure reference designator
     */
    getStatusHelper(infraRD) {
      const infraSubstring = infraRD.slice(9, 14);
      let statusValue = _.get(this.infraIndicators, infraSubstring);
      if (_.isEmpty(statusValue)) {
        statusValue = 'unset';
      }
      return statusValue.toLowerCase();
    },
    /**
     * Takes the status information and displays "Status" text
     * @param {string} infraRD - infrastructure reference designator
     */
    getStatusIndicatorText(infraRD) {
      const statusHolderText = this.getStatusHelper(infraRD);
      const newStr = statusHolderText.replace(/-|_/g, ' ');
      let text;
      if (_.isEmpty(statusHolderText)) {
        text = 'No status available';
      } else {
        text = newStr[0].toUpperCase() + newStr.substring(1);
      }
      return text;
    },
  },
  computed: {
    ...mapState({
      infraIndicators: (state) => state.infraIndicators,
    }),
    /**
     * Gets the wordpress link if available
     */
    pageLink() {
      if (this.wpContent) {
        if (!Array.isArray(this.wpContent)) {
          return this.wpContent.link;
        }
      }
      return null;
    },
    /**
     * Gets the wordpress excerpt if available
     */
    excerpt() {
      if (this.wpContent) {
        if (!Array.isArray(this.wpContent)) {
          return this.wpContent.excerpt.rendered;
        }
      }
      return null;
    },
  },
};
</script>

<style>
.infra-attribute {
  background-color: rgba(211, 211, 211, 0.3);
  border-radius: 0.5rem;
  margin: 0.3rem 0 0.3rem 0;
  padding: 0.7rem;
}

.lightbox-header {
    height: 2rem !important;
    padding-right: 0.6rem !important;
    padding-top: 0.2rem !important;
    padding-bottom: 0.2rem !important;
    border-bottom: none !important;
}

.lightbox-body {
    padding-top: 0 !important;
}

.lightbox-modal-content {
  background: none !important;
  border: none !important;
}

.lightbox-modal-content button.close {
  color: #fff !important;
  text-shadow: none !important;
  opacity: 1 !important;
}
</style>
